import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, CircularProgress, Divider, Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import { useLanguage } from '../../../context/LanguageContext';
import { PageContainer, PageTitle } from '../../../components/Common';
import useAddStore from '../../../hooks/Magasins/useAddStore';
import { StoreInformationForm, AddressForm, ContactForm, StoreFormData, AddressFormData, ContactFormData } from './components';
import { useStoreList } from '../../../context/StoreListContext';

const INITIAL_STORE_DATA: StoreFormData = {
    currency_id: '1',
    identifier: '',
    name: '',
    entity_type: 'STORE',
    subscribe_date: '',
    unsubscribe_date: '',
    franchise: '0',
    signature: '',
    entity_number: '',
    latitude: '',
    longitude: '',
    transaction_integration_mode: '0',
    opening_hours: '',
    description: '',
    payment_overdue: '0',
    add_depend_on: [],
};

const INITIAL_ADDRESS_DATA: AddressFormData = {
    address1: '',
    address2: '',
    address3_type: '',
    address3_num: '',
    address3_name: '',
    address4: '',
    zip_code: '',
    local_city_name: '',
    short_city_name: '',
    local_country_name: 'FRANCE',
};

const INITIAL_CONTACT_DATA: ContactFormData = {
    phone_number: '',
    fax_number: '',
    dialing_code: '+33',
    email_address: '',
};

const AddNewStore = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { addStore, loading, reset } = useAddStore();
  const { stores, loading: storesLoading } = useStoreList();

  const [storeData, setStoreData] = useState<StoreFormData>(INITIAL_STORE_DATA);
  const [addressData, setAddressData] = useState<AddressFormData>(INITIAL_ADDRESS_DATA);
  const [addressResetKey, setAddressResetKey] = useState(0);
  const [contactData, setContactData] = useState<ContactFormData>(INITIAL_CONTACT_DATA);

  const listStoresOptions = useMemo(() => {
    if (!stores) return [];
    return stores.map((mag: any) => ({
      value: mag.ENTITY_ID,
      label: `${mag.IDENTIFIER} - ${mag.NAME}`,
      data: {
        ENTITY_ID: mag.ENTITY_ID,
        IDENTIFIER: mag.IDENTIFIER,
        NAME: mag.NAME,
        LOCAL_CITY_NAME: mag.LOCAL_CITY_NAME,
        ENTITY_TYPE: mag.ENTITY_TYPE || null
      },
    }));
  }, [stores]);

  const handleStoreChange = useCallback((field: keyof StoreFormData, value: string | number[]) => {
      setStoreData((prev: StoreFormData) => ({ ...prev, [field]: value }));
  }, []);

  const handleAddressChange = useCallback((field: keyof AddressFormData, value: string) => {
      setAddressData((prev: AddressFormData) => ({ ...prev, [field]: value }));
  }, []);

  const handleContactChange = useCallback((field: keyof ContactFormData, value: string) => {
      setContactData((prev: ContactFormData) => ({ ...prev, [field]: value }));
  }, []);

  const storeValidationIssues = useMemo(() => {
      const issues: string[] = [];
      const identifier = storeData.identifier.trim();
      const name = storeData.name.trim();
      const subscribeDate = storeData.subscribe_date;

      if(!subscribeDate){
          issues.push(t('pages.store.subscribeDate'));
      }

      if (!identifier) {
          issues.push(t('pages.store.storeNumber'));
      }

      if (!name) {
          issues.push(t('pages.store.name'));
      }

      if (storeData.identifier.length > 10) {
          issues.push(t('pages.store.errors.identifierTooLong'));
      }

      if (storeData.name.length > 50) {
          issues.push(t('pages.store.errors.nameTooLong'));
      }

      const storeExists = stores?.some(
        (store: any) => String(store.IDENTIFIER ?? '').toLowerCase() === identifier.toLowerCase()
      );

      if (identifier && storeExists) {
          issues.push(t('pages.store.errors.identifierExists'));
      }

      return issues;
  }, [storeData.identifier, storeData.name, storeData.subscribe_date, stores, t]);

  const submitDisabled = loading || storeValidationIssues.length > 0;
  const submitTooltip = storeValidationIssues.length > 0 ? (
      <Box>
          <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('pages.store.fieldsToComplete')}
          </Typography>
          {storeValidationIssues.map((issue) => (
              <Typography key={issue} variant="caption" component="div">
                  - {issue}
              </Typography>
          ))}
      </Box>
  ) : '';

  const validateForm = useCallback((): boolean => {
      if (!storeData.identifier.trim() || !storeData.name.trim()) {
          toast.error(t('pages.store.errors.requiredFields'));
          return false;
      }
      if (storeData.identifier.length > 10) {
          toast.error(t('pages.store.errors.identifierTooLong'));
          return false;
      }
      if (storeData.name.length > 50) {
          toast.error(t('pages.store.errors.nameTooLong'));
          return false;
      }
      
      // Vérifier si l'identifiant existe déjà
      const storeExists = stores?.some(
        (store: any) => String(store.IDENTIFIER ?? '').toLowerCase() === storeData.identifier.trim().toLowerCase()
      );
      if (storeExists) {
        toast.error(t('pages.store.errors.identifierExists'));
        return false;
      }

      return true;
  }, [storeData, stores, t]);

  const handleReset = useCallback(() => {
      setStoreData(INITIAL_STORE_DATA);
      setAddressData(INITIAL_ADDRESS_DATA);
      setAddressResetKey(key => key + 1);
      setContactData(INITIAL_CONTACT_DATA);
      reset();
  }, [reset]);

  const handleSubmit = useCallback(async () => {
      if (!validateForm()) return;

      const toastId = toast.loading(t('pages.store.creating'));

      const { dialing_code, phone_number, fax_number, email_address } = contactData;
      const cleanedContactData = { dialing_code, phone_number, fax_number, email_address };
      
      const payload = { 
          ...storeData, 
          add_depend_on: storeData.add_depend_on.join(','), 
          ...addressData, 
          ...cleanedContactData 
      };
      const result = await addStore(payload);

      if (result) {
          const storeId = (result as any).entityId || result.ENTITY_ID;
          const storeIdentifier = (result as any).identifier || result.IDENTIFIER;
          const storeName = (result as any).name || result.NAME;

          toast.dismiss(toastId);
          
          toast.success(
              <div>
                  {t('pages.store.success.storeCreated')}
                  <div style={{ marginTop: '10px' }}>
                      <Button size="small" variant="contained" onClick={() => {
                          toast.dismiss();
                          navigate('/store/show-store', { state: { storeId, storeIdentifier, storeName } });
                      }}>
                          {t('pages.store.viewStore')}
                      </Button>
                  </div>
              </div>,
              { autoClose: 8000, closeOnClick: false }
          );
          handleReset();
      } else {
          toast.update(toastId, {
            render: t('pages.store.errors.createFailed'),
            type: 'error',
            isLoading: false,
            autoClose: 5000
          });
      }
  }, [storeData, addressData, contactData, addStore, validateForm, navigate, t, handleReset]);
  
  return (
    <PageContainer>
      <PageTitle>{t('pages.store.addNewStore')}</PageTitle>

      <Card sx={{ mb: 2 }}>
          <CardContent>
              <Typography variant="subtitle1" fontWeight={600} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.78rem' }}>
                  {t('pages.store.storeSection')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <StoreInformationForm 
                data={storeData} 
                onChange={handleStoreChange}
                listStoresOptions={listStoresOptions}
                storesLoading={storesLoading}
              />
          </CardContent>
      </Card>
      
      <Card sx={{ mb: 2 }}>
          <CardContent>
              <Typography variant="subtitle1" fontWeight={600} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.78rem' }}>
                  {t('pages.store.coordinatesSection')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <AddressForm key={addressResetKey} data={addressData} onChange={handleAddressChange} />
          </CardContent>
      </Card>
      
      <Card sx={{ mb: 2 }}>
          <CardContent>
              <Typography variant="subtitle1" fontWeight={600} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.78rem' }}>
                  {t('pages.store.diversSection')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <ContactForm data={contactData} onChange={handleContactChange} />
          </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title={submitTooltip} arrow disableHoverListener={!storeValidationIssues.length}>
          <span>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSubmit}
              disabled={submitDisabled} 
              sx={{ 
                py: 1.5, 
                minWidth: '250px',
                minHeight: '42px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {loading && <CircularProgress size={20} color="inherit" />}
              {loading ? t('common.saving') : t('pages.store.createStore')}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </PageContainer>
  );
};

export default AddNewStore;
